import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '_store';
import { apiFetch } from 'api/client';
import 'profile/profile.css';
import './organization-settings.css';
import { KEY_SCOPE_OPTIONS } from './apiKeyScopes';

export { OrganizationSettings };

function OrganizationSettings() {
    const { authUser, selectedOrganization, setOrgPreferences } = useContext(AuthContext);
    const [apiKeys, setApiKeys] = useState([]);
    const [keysLoading, setKeysLoading] = useState(false);
    const [keysError, setKeysError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyPerms, setNewKeyPerms] = useState(['lists']);
    const [newKeyExpiry, setNewKeyExpiry] = useState('0');
    const [creating, setCreating] = useState(false);
    const [createdKey, setCreatedKey] = useState(null);
    const [copied, setCopied] = useState(false);

    // Preferences state
    const [flowxEnabled, setFlowxEnabled] = useState(false);
    const [flowxUrl, setFlowxUrl] = useState('');
    const [prefsLoading, setPrefsLoading] = useState(false);
    const [prefsSaving, setPrefsSaving] = useState(false);
    const [prefsError, setPrefsError] = useState(null);
    const [prefsSaved, setPrefsSaved] = useState(false);

    const organizations = useMemo(() => (
        Array.isArray(authUser?.organizations) ? authUser.organizations : []
    ), [authUser]);

    const currentOrganization = useMemo(() => {
        if (organizations.length === 0) {
            return null;
        }

        return organizations.find((organization) =>
            String(organization.id ?? organization.name) === String(selectedOrganization)
        ) ?? organizations[0];
    }, [organizations, selectedOrganization]);

    const currentOrganizationId = currentOrganization?.id ?? '';
    const currentOrganizationName = currentOrganization?.name || currentOrganization?.displayName || 'Organization';

    const loadApiKeys = useCallback(async () => {
        if (!currentOrganizationId) {
            setApiKeys([]);
            return;
        }

        setKeysLoading(true);
        setKeysError(null);

        try {
            const response = await apiFetch('/auth/api-keys');

            if (!response.ok) {
                throw new Error(await getErrorMessage(response, 'Failed to load API keys'));
            }

            const data = await response.json();
            const keys = Array.isArray(data) ? data : (data.results ?? data.api_keys ?? data.keys ?? []);
            setApiKeys(keys.filter((key) => String(key.organization_id) === String(currentOrganizationId)));
        } catch (error) {
            setKeysError(error.message);
        } finally {
            setKeysLoading(false);
        }
    }, [currentOrganizationId]);

    useEffect(() => {
        loadApiKeys();
    }, [loadApiKeys]);

    useEffect(() => {
        setShowCreateForm(false);
        setCreatedKey(null);
        setCopied(false);
    }, [currentOrganizationId]);

    // Load preferences when org changes
    const loadPreferences = useCallback(async () => {
        if (!currentOrganizationId) return;
        setPrefsLoading(true);
        setPrefsError(null);
        try {
            const res = await apiFetch(`/auth/organization/${currentOrganizationId}/preferences`);
            if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to load preferences'));
            const data = await res.json();
            setFlowxEnabled(!!data.flowx_enabled);
            setFlowxUrl(data.flowx_url || '');
        } catch (err) {
            setPrefsError(err.message);
        } finally {
            setPrefsLoading(false);
        }
    }, [currentOrganizationId]);

    useEffect(() => {
        loadPreferences();
        setPrefsSaved(false);
    }, [loadPreferences]);

    const handleSavePreferences = async (event) => {
        event.preventDefault();
        if (!currentOrganizationId) return;
        setPrefsSaving(true);
        setPrefsError(null);
        setPrefsSaved(false);
        try {
            const res = await apiFetch(`/auth/organization/${currentOrganizationId}/preferences`, {
                method: 'PUT',
                json: { flowx_enabled: flowxEnabled, flowx_url: flowxUrl },
            });
            if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to save preferences'));
            setOrgPreferences({ flowx_enabled: flowxEnabled, flowx_url: flowxUrl });
            setPrefsSaved(true);
            window.setTimeout(() => setPrefsSaved(false), 3000);
        } catch (err) {
            setPrefsError(err.message);
        } finally {
            setPrefsSaving(false);
        }
    };

    const handleCreateKey = async (event) => {
        event.preventDefault();
        if (!newKeyName.trim() || newKeyPerms.length === 0 || !currentOrganizationId) {
            return;
        }

        setCreating(true);
        setKeysError(null);

        try {
            const response = await apiFetch('/auth/api-keys', {
                method: 'POST',
                json: {
                    name: newKeyName.trim(),
                    organization_id: Number(currentOrganizationId),
                    scopes: newKeyPerms.join(','),
                    expires_in_days: Number(newKeyExpiry)
                }
            });

            if (!response.ok) {
                throw new Error(await getErrorMessage(response, 'Failed to create API key'));
            }

            const data = await response.json();
            setCreatedKey(data.api_key?.key ?? data.key ?? null);
            setNewKeyName('');
            setNewKeyPerms(['lists']);
            setNewKeyExpiry('0');
            setShowCreateForm(false);
            loadApiKeys();
        } catch (error) {
            setKeysError(error.message);
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteKey = async (id) => {
        if (!window.confirm('Delete this API key? This cannot be undone.')) {
            return;
        }

        setKeysError(null);

        try {
            const response = await apiFetch(`/auth/api-keys/${id}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error(await getErrorMessage(response, 'Failed to delete API key'));
            }

            loadApiKeys();
        } catch (error) {
            setKeysError(error.message);
        }
    };

    const togglePerm = (permission) => {
        setNewKeyPerms((previous) =>
            previous.includes(permission)
                ? previous.filter((value) => value !== permission)
                : [...previous, permission]
        );
    };

    const handleCopy = async () => {
        if (!createdKey) {
            return;
        }

        await navigator.clipboard.writeText(createdKey);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="profile-page">
            <div className="profile-hero">
                <div>
                    <div className="profile-breadcrumb">
                        <span className="profile-breadcrumb-dot"></span>
                        <span className="profile-breadcrumb-text">Account / Organization Settings</span>
                    </div>
                    <h1 className="profile-title">Organization Settings</h1>
                </div>
            </div>

            <div className="org-settings-identity-card">
                <div className="org-settings-org-icon">
                    <span className="material-symbols-outlined">apartment</span>
                </div>
                <div className="org-settings-identity-info">
                    <div className="org-settings-org-name">{currentOrganizationName}</div>
                    <div className="org-settings-meta">
                        API keys belong to the active organization and are visible to its members.
                    </div>
                </div>
            </div>

            {!currentOrganization ? (
                <div className="profile-info-banner">
                    <span className="material-symbols-outlined text-sm">info</span>
                    No organization is selected for this account.
                </div>
            ) : (
                <div className="profile-card profile-card-full">
                    <div className="profile-card-header">
                        <span className="material-symbols-outlined profile-card-icon">settings</span>
                        <div className="org-settings-card-copy">
                            <h2 className="profile-card-title">Integrations</h2>
                            <p className="profile-card-desc">Configure integrations for {currentOrganizationName}.</p>
                        </div>
                    </div>

                    {prefsLoading ? (
                        <p className="profile-hint">
                            <span className="material-symbols-outlined text-sm lists-spin">sync</span>
                            Loading preferences…
                        </p>
                    ) : (
                        <form className="org-settings-pref-form" onSubmit={handleSavePreferences}>
                            <div className="org-settings-pref-row">
                                <div className="org-settings-pref-info">
                                    <span className="material-symbols-outlined org-settings-pref-icon">monitoring</span>
                                    <div>
                                        <div className="org-settings-pref-label">FlowX Netflow Collector</div>
                                        <div className="org-settings-pref-desc">Enable the FlowX netflow collector integration. When enabled, FlowX appears in the navigation.</div>
                                    </div>
                                </div>
                                <label className="org-settings-toggle">
                                    <input
                                        type="checkbox"
                                        checked={flowxEnabled}
                                        onChange={(e) => setFlowxEnabled(e.target.checked)}
                                        className="org-settings-toggle-input"
                                    />
                                    <span className="org-settings-toggle-track"></span>
                                </label>
                            </div>

                            {flowxEnabled && (
                                <ProfileField label="FlowX URL">
                                    <input
                                        type="url"
                                        className="profile-input"
                                        placeholder="http://flowx.example.com:2055"
                                        value={flowxUrl}
                                        onChange={(e) => setFlowxUrl(e.target.value)}
                                    />
                                </ProfileField>
                            )}

                            {prefsError && <ProfileError msg={prefsError} />}

                            <div className="org-settings-pref-actions">
                                {prefsSaved && (
                                    <span className="org-settings-pref-saved">
                                        <span className="material-symbols-outlined text-sm">check_circle</span>
                                        Saved
                                    </span>
                                )}
                                <button type="submit" className="profile-btn-primary" disabled={prefsSaving}>
                                    {prefsSaving
                                        ? <><span className="material-symbols-outlined text-sm lists-spin">sync</span> Saving…</>
                                        : <><span className="material-symbols-outlined text-sm">save</span> Save Preferences</>
                                    }
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}

            {!currentOrganization ? null : (
                <div className="profile-card profile-card-full org-settings-api-keys-card">
                    <div className="profile-card-header">
                        <span className="material-symbols-outlined profile-card-icon">key</span>
                        <div className="org-settings-card-copy">
                            <h2 className="profile-card-title">API Keys</h2>
                            <p className="profile-card-desc">Manage keys for {currentOrganizationName}.</p>
                        </div>
                        {!showCreateForm && (
                            <button
                                className="profile-btn-primary"
                                type="button"
                                onClick={() => {
                                    setShowCreateForm(true);
                                    setCreatedKey(null);
                                }}
                            >
                                <span className="material-symbols-outlined text-sm">add</span>
                                New API Key
                            </button>
                        )}
                    </div>

                    {createdKey && (
                        <div className="profile-key-reveal">
                            <div className="profile-key-reveal-header">
                                <span className="material-symbols-outlined" style={{ color: 'var(--color-warning-icon)' }}>warning</span>
                                <strong>Copy your key now. It will not be shown again.</strong>
                            </div>
                            <div className="profile-key-reveal-row">
                                <code className="profile-key-value">{createdKey}</code>
                                <button className="profile-btn-secondary" type="button" onClick={handleCopy}>
                                    <span className="material-symbols-outlined text-sm">{copied ? 'check' : 'content_copy'}</span>
                                    {copied ? 'Copied' : 'Copy'}
                                </button>
                            </div>
                            <button className="profile-key-dismiss" type="button" onClick={() => setCreatedKey(null)}>Dismiss</button>
                        </div>
                    )}

                    {keysError && <ProfileError msg={keysError} />}

                    {showCreateForm && (
                        <form className="profile-apikey-form" onSubmit={handleCreateKey}>
                            <ProfileField label="Organization">
                                <input type="text" className="profile-input disabled" value={currentOrganizationName} disabled />
                            </ProfileField>

                            <ProfileField label="Key Name" required>
                                <input
                                    type="text"
                                    className="profile-input"
                                    placeholder="e.g. ci-pipeline"
                                    value={newKeyName}
                                    onChange={(event) => setNewKeyName(event.target.value)}
                                    required
                                />
                            </ProfileField>

                            <fieldset className="profile-field org-settings-scopes-field">
                                <legend className="profile-field-label">Scopes *</legend>
                                <p id="api-key-scopes-hint" className="org-settings-scopes-hint">
                                    Choose one or more services this key can access.
                                </p>
                                <div className="org-settings-scope-grid">
                                    {KEY_SCOPE_OPTIONS.map(({ value, label, description }) => {
                                        const selected = newKeyPerms.includes(value);

                                        return (
                                            <label
                                                key={value}
                                                className={`org-settings-scope-option${selected ? ' selected' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    onChange={() => togglePerm(value)}
                                                    className="profile-perm-checkbox"
                                                    aria-describedby="api-key-scopes-hint"
                                                />
                                                <span className="org-settings-scope-copy">
                                                    <span className="org-settings-scope-name">{label}</span>
                                                    <span className="org-settings-scope-description">{description}</span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </fieldset>

                            <ProfileField label="Expires">
                                <select
                                    className="profile-select"
                                    value={newKeyExpiry}
                                    onChange={(event) => setNewKeyExpiry(event.target.value)}
                                >
                                    <option value="0">Never</option>
                                    <option value="1">1 day</option>
                                    <option value="7">7 days</option>
                                    <option value="30">30 days</option>
                                    <option value="90">90 days</option>
                                    <option value="365">1 year</option>
                                </select>
                            </ProfileField>

                            <div className="profile-form-actions">
                                <button
                                    type="submit"
                                    className="profile-btn-primary"
                                    disabled={creating || !newKeyName.trim() || newKeyPerms.length === 0}
                                >
                                    {creating
                                        ? <><span className="material-symbols-outlined text-sm lists-spin">sync</span> Creating…</>
                                        : <><span className="material-symbols-outlined text-sm">add</span> Create Key</>
                                    }
                                </button>
                                <button
                                    type="button"
                                    className="profile-btn-secondary"
                                    onClick={() => {
                                        setShowCreateForm(false);
                                        setNewKeyName('');
                                        setNewKeyPerms(['lists']);
                                        setNewKeyExpiry('0');
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}

                    {keysLoading ? (
                        <p className="profile-hint">
                            <span className="material-symbols-outlined text-sm lists-spin">sync</span>
                            Loading keys…
                        </p>
                    ) : apiKeys.length === 0 ? (
                        <p className="profile-hint">No API keys for this organization yet.</p>
                    ) : (
                        <div className="lists-table-card org-settings-table-card">
                            <div className="lists-table-wrap">
                                <table className="lists-table">
                                    <thead>
                                        <tr className="lists-table-head-row">
                                            <th className="lists-th">Name</th>
                                            <th className="lists-th">Prefix</th>
                                            <th className="lists-th">Scopes</th>
                                            <th className="lists-th">Created By</th>
                                            <th className="lists-th">Expires</th>
                                            <th className="lists-th">Last Used</th>
                                            <th className="lists-th">Status</th>
                                            <th className="lists-th"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {apiKeys.map((key) => (
                                            <tr key={key.id} className="lists-table-row" style={{ cursor: 'default' }}>
                                                <td className="lists-td"><span className="lists-row-name">{key.name}</span></td>
                                                <td className="lists-td"><code className="lists-row-desc org-settings-code">{key.key_prefix}</code></td>
                                                <td className="lists-td"><span className="lists-row-sync">{key.scopes || '—'}</span></td>
                                                <td className="lists-td"><span className="lists-row-time">{key.created_by_username || '—'}</span></td>
                                                <td className="lists-td"><span className="lists-row-time">{key.expires_at ? new Date(key.expires_at).toLocaleDateString() : 'Never'}</span></td>
                                                <td className="lists-td"><span className="lists-row-time">{key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : '—'}</span></td>
                                                <td className="lists-td">
                                                    <span className={`lists-status-pill ${key.active ? 'active' : 'inactive'}`}>
                                                        {key.active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="lists-td text-right">
                                                    <button
                                                        className="lists-icon-btn danger"
                                                        title="Delete key"
                                                        type="button"
                                                        onClick={() => handleDeleteKey(key.id)}
                                                    >
                                                        <span className="material-symbols-outlined">delete</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function ProfileField({ label, required, children }) {
    return (
        <div className="profile-field">
            <label className="profile-field-label">{label}{required && ' *'}</label>
            {children}
        </div>
    );
}

function ProfileError({ msg }) {
    return (
        <div className="profile-error-banner">
            <span className="material-symbols-outlined text-sm">error</span>
            {msg}
        </div>
    );
}

async function getErrorMessage(response, fallbackMessage) {
    try {
        const data = await response.json();
        return data?.error || data?.message || fallbackMessage;
    } catch {
        return fallbackMessage;
    }
}
